import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { authGuard } from '@core/navigation/guards/auth.guard';
import { loginGuard } from '@core/navigation/guards/login.guard';
import { LV2_PATHS } from '@core/navigation/navigation.constants';
import { onboardingResolver } from '@core/navigation/resolvers/onboarding.resolver';
import { NotFoundQueryParams } from '@core/navigation/types/navigation.types';
import {
  loadTranslation,
  OBJECT_DETAILS_TRANSLATIONS,
  provideTranslations,
  TranslationsSubfolder,
} from '@core/translation/translation.providers';

export const routes: Routes = [
  { path: '', redirectTo: LV2_PATHS.HOME, pathMatch: 'full' },
  {
    path: LV2_PATHS.LOGIN,
    loadComponent: () => import('./pages/login/login-shell.page').then(({ LoginShellPage }) => LoginShellPage),
    canActivate: [loginGuard],
    resolve: {
      translations: () => loadTranslation(),
    },
    providers: [...provideTranslations(TranslationsSubfolder.LOGIN)],
    children: [
      // No `''` child: LoginShellPage loads the auth providers (showing its own loader), then sends
      // a bare `/login` on to `proconnect`, `form` or `no-provider` for the enabled method.
      {
        path: 'proconnect',
        loadComponent: () =>
          import('./pages/login/login-proconnect/login-proconnect.page').then(
            ({ LoginProconnectPage }) => LoginProconnectPage,
          ),
      },
      {
        path: 'form',
        loadComponent: () =>
          import('./pages/login/login-form/login-form.page').then(({ LoginFormPage }) => LoginFormPage),
      },
      {
        path: 'no-provider',
        loadComponent: () =>
          import('./pages/login/no-provider/no-provider.page').then(({ LoginNoProviderPage }) => LoginNoProviderPage),
      },
    ],
  },
  {
    path: LV2_PATHS.REGISTER_CHECK_EMAIL,
    loadComponent: () =>
      import('./pages/login/register/check-email/check-email.page').then(({ CheckEmailPage }) => CheckEmailPage),
    resolve: {
      translations: () => loadTranslation(),
    },
    providers: [...provideTranslations(TranslationsSubfolder.LOGIN)],
  },
  {
    path: LV2_PATHS.REGISTER,
    loadComponent: () =>
      import('./pages/login/register/register/register.page').then(({ RegisterPage }) => RegisterPage),
    resolve: {
      translations: () => loadTranslation(),
    },
    providers: [...provideTranslations(TranslationsSubfolder.LOGIN)],
  },
  {
    path: LV2_PATHS.VERIFY_EMAIL,
    loadComponent: () =>
      import('./pages/login/register/verify-email/verify-email.page').then(({ VerifyEmailPage }) => VerifyEmailPage),
    resolve: {
      translations: () => loadTranslation(),
    },
    providers: [...provideTranslations(TranslationsSubfolder.LOGIN)],
  },
  {
    path: LV2_PATHS.FORGOT_PASSWORD,
    loadComponent: () =>
      import('./pages/login/forgot-password/forgot-password/forgot-password.page').then(
        ({ ForgotPasswordPage }) => ForgotPasswordPage,
      ),
    resolve: {
      translations: () => loadTranslation(),
    },
    providers: [...provideTranslations(TranslationsSubfolder.LOGIN)],
  },
  {
    path: LV2_PATHS.RESET_PASSWORD,
    loadComponent: () =>
      import('./pages/login/forgot-password/reset-password/reset-password.page').then(
        ({ ResetPasswordPage }) => ResetPasswordPage,
      ),
    resolve: {
      translations: () => loadTranslation(),
    },
    providers: [...provideTranslations(TranslationsSubfolder.LOGIN)],
  },
  {
    path: LV2_PATHS.ACCOUNT_STATUS,
    loadComponent: () =>
      import('./pages/login/account-status/account-status.page').then(({ AccountStatusPage }) => AccountStatusPage),
    resolve: {
      translations: () => loadTranslation(),
    },
    providers: [...provideTranslations(TranslationsSubfolder.LOGIN)],
  },
  {
    path: '',
    loadComponent: () =>
      import('./core/navigation/ui/layout/main-navigation-layout/main-navigation-layout.component').then(
        ({ MainNavigationLayoutComponent }) => MainNavigationLayoutComponent,
      ),
    canActivate: [authGuard],
    resolve: {
      onboarding: onboardingResolver,
    },
    children: [
      {
        // Global drawer host: overlays a query-param drawer over any authenticated page.
        // Loads the object-details translations here (once, regardless of route) so drawers — and the
        // full-page action/folder views — inherit them via the injector chain.
        path: '',
        loadComponent: () =>
          import('./core/navigation/ui/layout/drawer-view-layout/drawer-view-layout.component').then(
            ({ DrawerViewLayoutComponent }) => DrawerViewLayoutComponent,
          ),
        resolve: {
          translations: () => loadTranslation(),
        },
        providers: [...provideTranslations(...OBJECT_DETAILS_TRANSLATIONS)],
        children: [
          {
            path: LV2_PATHS.HOME,
            loadComponent: () => import('./pages/home/home.page').then(({ HomePage }) => HomePage),
            resolve: {
              translations: () => loadTranslation(),
            },
            providers: [...provideTranslations(TranslationsSubfolder.DASHBOARD, TranslationsSubfolder.OBJECT_DETAILS)],
          },
          {
            path: LV2_PATHS.ACTIONS,
            loadChildren: () => import('./pages/actions/actions.routes').then(({ routes }) => routes),
          },
          {
            path: LV2_PATHS.COMMUNITIES,
            resolve: {
              translations: () => loadTranslation(),
            },
            loadChildren: () => import('./pages/communities/communities.routes').then(({ routes }) => routes),

            providers: [
              ...provideTranslations(
                TranslationsSubfolder.COMMUNITIES,
                TranslationsSubfolder.TABLE,
                TranslationsSubfolder.OPERATOR,
                TranslationsSubfolder.OBJECT_DETAILS,
              ),
            ],
          },
          {
            path: LV2_PATHS.SITES,
            resolve: {
              translations: () => loadTranslation(),
            },
            loadChildren: () => import('./pages/sites/sites.routes').then(({ routes }) => routes),
            providers: [
              ...provideTranslations(
                TranslationsSubfolder.SITES,
                TranslationsSubfolder.TABLE,
                TranslationsSubfolder.OPERATOR,
                TranslationsSubfolder.OBJECT_DETAILS,
              ),
            ],
          },
          {
            path: LV2_PATHS.AGENTS,
            resolve: {
              translations: () => loadTranslation(),
            },
            loadChildren: () => import('./pages/agents/agents.routes').then(({ routes }) => routes),
            providers: [
              ...provideTranslations(
                TranslationsSubfolder.AGENTS,
                TranslationsSubfolder.TABLE,
                TranslationsSubfolder.OPERATOR,
                TranslationsSubfolder.OBJECT_DETAILS,
              ),
            ],
          },
          {
            path: LV2_PATHS.FOLDERS,
            loadChildren: () => import('./pages/folders/folders.routes').then(({ routes }) => routes),
          },
          {
            path: LV2_PATHS.SPENDINGS,
            resolve: {
              translations: () => loadTranslation(),
            },
            loadChildren: () => import('./pages/spendings/spendings.routes').then(({ routes }) => routes),
            providers: [...provideTranslations(TranslationsSubfolder.SPENDINGS)],
          },
          {
            path: LV2_PATHS.PAYMENTS,
            resolve: {
              translations: () => loadTranslation(),
            },
            loadChildren: () => import('./pages/payments/payments.routes').then(({ routes }) => routes),
            providers: [...provideTranslations(TranslationsSubfolder.PAYMENTS)],
          },
        ],
      },
    ],
  },
  {
    path: LV2_PATHS.NOT_FOUND,
    resolve: {
      translations: () => loadTranslation(),
    },
    providers: [...provideTranslations(TranslationsSubfolder.NOT_FOUND)],
    loadComponent: () => import('./pages/not-found/not-found.page').then(({ NotFoundPage }) => NotFoundPage),
  },
  {
    path: '**',
    redirectTo: ({ url, queryParams }) => {
      const router = inject(Router);
      const triedUrl = router.serializeUrl(
        router.createUrlTree(
          url.map(({ path }) => path),
          { queryParams },
        ),
      );
      return router.createUrlTree([LV2_PATHS.NOT_FOUND], { queryParams: { [NotFoundQueryParams.URL]: triedUrl } });
    },
  },
];
